import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Alert, CircularProgress,
    Avatar, Chip, Card, CardContent, Divider,
    Radio, RadioGroup, Button, ChipDelete,
    Modal, ModalDialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/joy';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axiosConfig from '../util/axiosConfig';

const ORANGE = '#eb7a24';

const CITIES = [
    { code: 'hyd', name: 'Hyderabad' },
    { code: 'blr', name: 'Bangalore' },
    { code: 'chn', name: 'Chennai' },
    { code: 'pun', name: 'Pune' },
    { code: 'coc', name: 'Cochin' },
    { code: 'bhb', name: 'Bhubaneswar' },
    { code: 'del', name: 'Delhi' },
    { code: 'ahm', name: 'Ahmedabad' },
    { code: 'kol', name: 'Kolkata' },
    { code: 'mum', name: 'Mumbai' },
    { code: 'bar', name: 'Baroda' },
    { code: 'gur', name: 'Gurgaon' },
    { code: 'noi', name: 'Noida' },
    { code: 'chd', name: 'Chandigarh' },
    { code: 'ind', name: 'Indore' },
    { code: 'coi', name: 'Coimbatore' },
    { code: 'jai', name: 'Jaipur' },
    { code: 'tri', name: 'Trivandrum' },
    { code: 'bhp', name: 'Bhopal' },
];

const SALARY_OPTIONS = [
    // { value: '1', label: '0.6 LPA to 0.99 LPA' },
    // { value: '2', label: '1.0 LPA to 1.49 LPA' },
    { value: '3', label: '1.5 LPA to 1.99 LPA' },
    { value: '4', label: '2.0 LPA to 2.49 LPA' },
    { value: '5', label: '2.5 LPA to 2.99 LPA' },
    { value: '6', label: '3.0 LPA & Above' },
];

const hasValue = (v) => !(v === undefined || v === null || v === '' || v === '0' || v === 0);
const pct = (v) => (hasValue(v) ? `${v}%` : null);

// registeredstudent.places/rplaces store full lowercase city names (searched via
// LIKE '%hyderabad%' by vectorindia's Search Students New); map legacy short
// codes back to their name so old data still round-trips.
const CITY_NAME_TO_CODE = CITIES.reduce((acc, { code, name }) => {
    acc[name.toLowerCase()] = code;
    return acc;
}, {});
const toCode = (s) => { const l = s.trim().toLowerCase(); return CITY_NAME_TO_CODE[l] || l; };

const initCityPrefs = () => {
    const prefs = {};
    CITIES.forEach(({ code }) => { prefs[code] = { salary: '', relocate: false }; });
    return prefs;
};

const parsePlacementPrefs = (pd) => {
    // pd.salary = "hyd:3,blr:4" — per-city salary from studentsalary table
    const salaryMap = {};
    (pd.salary || '').split(',').filter(Boolean).forEach(part => {
        const [code, sal] = part.split(':');
        if (code) salaryMap[code.trim()] = (sal || '').trim();
    });
    // pd.rplaces = "hyderabad,bangalore" — cities willing to relocate
    const relocateSet = new Set((pd.rplaces || '').split(',').filter(Boolean).map(toCode));
    const prefs = {};
    CITIES.forEach(({ code }) => {
        prefs[code] = { salary: salaryMap[code] || '', relocate: relocateSet.has(code) };
    });
    return prefs;
};

const ColHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, pb: 0.75, borderBottom: `2px solid ${ORANGE}` }}>
        {icon}
        <Typography level="title-sm" sx={{ color: ORANGE, fontWeight: 'bold' }}>{title}</Typography>
    </Box>
);

const ColInfoRow = ({ label, value }) => {
    if (!hasValue(value)) return null;
    return (
        <Box sx={{ py: 0.4, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
            <Typography level="body-xs" sx={{ color: '#9e9e9e' }}>{label}</Typography>
            <Typography level="body-sm" sx={{ color: '#404041', fontWeight: 'md', wordBreak: 'break-word' }}>{value}</Typography>
        </Box>
    );
};

const PlacementProfileEdit = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [placementData, setPlacementData] = useState(null);
    const [canEdit, setCanEdit] = useState(false);

    const [available, setAvailable] = useState(null);
    const [cityPrefs, setCityPrefs] = useState(initCityPrefs());
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [profileRes, placementRes, statusRes] = await Promise.all([
                    axiosConfig.get('/students/profile'),
                    axiosConfig.get('/students/placement-profile'),
                    axiosConfig.get('/students/profile/placement-status'),
                ]);
                setProfileData(profileRes.data);
                setPlacementData(placementRes.data);
                setCanEdit(statusRes.data.isPlacementProfileOpen === true);

                const pd = placementRes.data;
                setAvailable(pd.placement ? 'yes' : 'no');
                setCityPrefs(parsePlacementPrefs(pd));

                setPhotoUrl(profileRes.data?.photoUrl || null);
            } catch (err) {
                setError(err.response?.data?.errorMessage || 'Failed to load profile data');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();

        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setSaveMsg(null);
        try {
            const selectedCities = available === 'yes'
                ? CITIES.filter(({ code }) => cityPrefs[code]?.salary)
                : [];
            // registeredstudent.places = comma-separated full city names (lowercase)
            const places = selectedCities.map(({ name }) => name.toLowerCase()).join(',');
            // studentsalary.salary = city:salaryCode pairs (short codes; unrelated to places/rplaces)
            const salary = selectedCities.map(({ code }) => `${code}:${cityPrefs[code].salary}`).join(',');
            const rplaces = available === 'yes'
                ? CITIES.filter(({ code }) => cityPrefs[code]?.relocate).map(({ name }) => name.toLowerCase()).join(',')
                : '';

            await axiosConfig.put('/students/placement-profile', {
                placement: available === 'yes' ? 1 : 0,
                places,
                salary,
                rplaces,
            });
            setSaveMsg({ type: 'success', text: 'Placement preferences saved successfully.' });
            setCanEdit(false);
            setTimeout(() => navigate('/placement-profile'), 1200);
        } catch (err) {
            setSaveMsg({ type: 'error', text: err.response?.data?.errorMessage || 'Failed to save placement preferences.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: 2 }}>
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">{error}</Alert>
            </Box>
        );
    }

    const p = profileData || {};
    const pd = placementData || {};

    const ugFields = [
        { label: 'Qualification', value: !p.hasPg ? p.qualification : null },
        { label: 'Specialization', value: p.ugspec },
        { label: 'Board / University', value: p.ugboard },
        { label: 'College', value: p.ugcollege },
        { label: 'City', value: p.ugcity },
        { label: 'State', value: p.ugstate },
        { label: 'Year of Passing', value: p.ugyop },
        { label: 'Certificate %', value: pct(p.ugcertper) },
        { label: 'Aggregate %', value: pct(p.ugaggrper) },
        { label: 'History of Backlogs', value: p.tugbacklog },
        { label: 'Current Backlogs', value: p.backlog },
    ];

    const pgFields = [
        { label: 'Qualification', value: p.pgqualification },
        { label: 'Specialization', value: p.pgspec },
        { label: 'Board / University', value: p.pgboard },
        { label: 'College', value: p.pgcollege },
        { label: 'City', value: p.pgcity },
        { label: 'State', value: p.pgstate },
        { label: 'Year of Passing', value: p.pgyop },
        { label: 'Certificate %', value: pct(p.pgcertper) },
        { label: 'Aggregate %', value: pct(p.pgaggrper) },
        { label: 'History of Backlogs', value: p.tpgbacklog },
    ];

    const tenthFields = [
        { label: 'Board', value: p.xboard },
        { label: 'Year of Passing', value: p.xyop },
        { label: 'Percentage', value: pct(p.tenthpercentage) },
    ];

    const twelfthFields = [
        { label: 'Board', value: p.xiiboard },
        { label: 'Year of Passing', value: p.xiiyop },
        { label: 'Percentage', value: pct(p.interpercentage) },
    ];

    const diplomaFields = [
        { label: 'Board', value: p.diplomaboard },
        { label: 'Year of Passing', value: p.diplomayop },
        { label: 'Percentage', value: p.diplomapercentage && p.diplomapercentage !== '0.00' ? `${p.diplomapercentage}%` : null },
    ];

    const hasTenth = tenthFields.some(f => hasValue(f.value));
    const hasTwelfth = twelfthFields.some(f => hasValue(f.value));
    const hasDiploma = diplomaFields.some(f => hasValue(f.value));
    const hasAcademic = hasTenth || hasTwelfth || hasDiploma;
    const colCount = 1 + (p.hasPg ? 1 : 0) + (hasAcademic ? 1 : 0);

    return (
        <Box sx={{ p: 2, maxWidth: '1100px', mx: 'auto', width: '100%' }}>

            {/* Page title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WorkIcon sx={{ color: ORANGE, fontSize: '2rem' }} />
                <Typography level="h2" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                    Placement Profile Update
                </Typography>
            </Box>

            {/* Profile card — banner + info columns */}
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar
                            src={photoUrl || undefined}
                            alt={p.name || 'Student Photo'}
                            sx={{
                                width: 68,
                                height: 68,
                                border: `2px solid ${ORANGE}`,
                                bgcolor: '#f19b30',
                                fontSize: '1.6rem',
                                '& img': { objectFit: 'cover', objectPosition: 'center' },
                            }}
                        >
                            {p.name ? p.name.charAt(0).toUpperCase() : 'S'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Typography level="h4" sx={{ color: '#404041' }}>{p.name || 'Student'}</Typography>
                                {hasValue(p.studentId || p.studentid) && (
                                    <Typography level="h4" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                                        {p.studentId || p.studentid}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: colCount > 1 ? '1fr 1fr' : '1fr',
                            lg: `repeat(${colCount}, 1fr)`,
                        },
                        gap: { xs: 2, sm: 3 },
                        alignItems: 'start',
                    }}>
                        {p.hasPg && (
                            <Box>
                                <ColHeader icon={<SchoolIcon sx={{ color: ORANGE, fontSize: '1.1rem' }} />} title="Post Graduation" />
                                {pgFields.some(f => hasValue(f.value))
                                    ? pgFields.map(f => <ColInfoRow key={f.label} label={f.label} value={f.value} />)
                                    : <Typography level="body-sm" sx={{ color: '#9e9e9e' }}>No PG details</Typography>
                                }
                            </Box>
                        )}

                        <Box>
                            <ColHeader icon={<SchoolIcon sx={{ color: ORANGE, fontSize: '1.1rem' }} />} title="Under Graduation" />
                            {ugFields.some(f => hasValue(f.value))
                                ? ugFields.map(f => <ColInfoRow key={f.label} label={f.label} value={f.value} />)
                                : <Typography level="body-sm" sx={{ color: '#9e9e9e' }}>No UG details</Typography>
                            }
                        </Box>

                        {hasAcademic && (
                            <Box>
                                <ColHeader icon={<PersonIcon sx={{ color: ORANGE, fontSize: '1.1rem' }} />} title="Academic Details" />
                                {hasTwelfth && (
                                    <>
                                        <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                            12th / Inter
                                        </Typography>
                                        {twelfthFields.map(f => <ColInfoRow key={f.label} label={f.label} value={f.value} />)}
                                    </>
                                )}
                                {hasDiploma && (
                                    <>
                                        <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 1.5, mb: 0.5 }}>
                                            Diploma
                                        </Typography>
                                        {diplomaFields.map(f => <ColInfoRow key={f.label} label={f.label} value={f.value} />)}
                                    </>
                                )}
                                {hasTenth && (
                                    <>
                                        <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 1.5, mb: 0.5 }}>
                                            10th Class
                                        </Typography>
                                        {tenthFields.map(f => <ColInfoRow key={f.label} label={f.label} value={f.value} />)}
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Placement Preferences Form */}
            <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, pb: 1, mb: 2, borderBottom: `2px solid ${ORANGE}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <WorkIcon sx={{ color: ORANGE, fontSize: '1.2rem' }} />
                            <Typography level="title-md" sx={{ color: ORANGE, fontWeight: 'bold' }}>
                                Placement Preferences
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { sm: 'auto' } }}>
                            <Typography level="body-sm" sx={{ fontWeight: 700, color: '#404041', whiteSpace: 'nowrap' }}>
                                Available for Placement :
                            </Typography>
                            <RadioGroup
                                orientation="horizontal"
                                value={available || ''}
                                onChange={(e) => setAvailable(e.target.value)}
                                sx={{ gap: 1.5 }}
                            >
                                <Radio value="yes" label="Yes" disabled={!canEdit} />
                                <Radio value="no" label="No" disabled={!canEdit} />
                            </RadioGroup>
                        </Box>
                    </Box>

                    {/* City-wise preferences — only when available for placement */}
                    {available === 'yes' && (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                            gap: 1.5,
                        }}>
                            {CITIES.map(({ code, name }) => {
                                const selected = !!cityPrefs[code]?.salary;
                                return (
                                    <Box key={code} sx={{
                                        border: '1px solid',
                                        borderColor: selected ? ORANGE : 'divider',
                                        borderRadius: 'md',
                                        p: 1.5,
                                        bgcolor: selected ? '#fff8f3' : 'background.surface',
                                        transition: 'border-color 0.2s, background-color 0.2s',
                                    }}>
                                        {/* City name */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25 }}>
                                            <LocationOnIcon sx={{ color: selected ? ORANGE : '#9e9e9e', fontSize: '1rem', transition: 'color 0.2s' }} />
                                            <Typography level="body-sm" sx={{ fontWeight: 700, color: selected ? ORANGE : '#404041', transition: 'color 0.2s' }}>
                                                {name}
                                            </Typography>
                                        </Box>

                                        {/* Expected Salary */}
                                        <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Expected Salary
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
                                            {SALARY_OPTIONS.map(opt => {
                                                const active = cityPrefs[code]?.salary === opt.value;
                                                return (
                                                    <Chip
                                                        key={opt.value}
                                                        size="sm"
                                                        variant={active ? 'solid' : 'outlined'}
                                                        endDecorator={active && canEdit
                                                            ? <ChipDelete
                                                                onDelete={() => setCityPrefs(prev => ({
                                                                    ...prev,
                                                                    [code]: { ...prev[code], salary: '' },
                                                                }))}
                                                                sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.15)' } }}
                                                            />
                                                            : null
                                                        }
                                                        onClick={() => {
                                                            if (!canEdit || active) return;
                                                            setCityPrefs(prev => ({
                                                                ...prev,
                                                                [code]: { ...prev[code], salary: opt.value },
                                                            }));
                                                        }}
                                                        sx={{
                                                            cursor: canEdit && !active ? 'pointer' : 'default',
                                                            bgcolor: active ? ORANGE : 'transparent',
                                                            borderColor: active ? ORANGE : 'divider',
                                                            color: active ? '#fff' : '#404041',
                                                            fontWeight: active ? 700 : 400,
                                                            '&:hover': canEdit && !active ? { borderColor: ORANGE, color: ORANGE } : {},
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </Chip>
                                                );
                                            })}
                                        </Box>

                                        {/* Willing to Relocate */}
                                        <Typography level="body-xs" sx={{ color: '#6e7072', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Willing to Relocate
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                                            {['Yes', 'No'].map(opt => {
                                                const active = opt === 'Yes' ? !!cityPrefs[code]?.relocate : !cityPrefs[code]?.relocate;
                                                return (
                                                    <Chip
                                                        key={opt}
                                                        size="sm"
                                                        variant={active ? 'solid' : 'outlined'}
                                                        onClick={() => {
                                                            if (!canEdit) return;
                                                            setCityPrefs(prev => ({
                                                                ...prev,
                                                                [code]: { ...prev[code], relocate: opt === 'Yes' },
                                                            }));
                                                        }}
                                                        sx={{
                                                            cursor: canEdit ? 'pointer' : 'default',
                                                            bgcolor: active ? (opt === 'Yes' ? '#2e7d32' : '#757575') : 'transparent',
                                                            borderColor: active ? (opt === 'Yes' ? '#2e7d32' : '#757575') : 'divider',
                                                            color: active ? '#fff' : '#404041',
                                                            fontWeight: active ? 700 : 400,
                                                            '&:hover': canEdit ? { borderColor: opt === 'Yes' ? '#2e7d32' : '#757575' } : {},
                                                        }}
                                                    >
                                                        {opt}
                                                    </Chip>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {saveMsg && (
                        <Alert color={saveMsg.type === 'success' ? 'success' : 'danger'} variant="soft" sx={{ mt: 2 }}>
                            {saveMsg.text}
                        </Alert>
                    )}

                    {canEdit && available !== null ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                            <Button
                                size="sm"
                                startDecorator={<SaveIcon />}
                                onClick={() => setConfirmOpen(true)}
                                sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#d06a1a' }, color: '#fff' }}
                            >
                                Save
                            </Button>
                        </Box>
                    ) : !canEdit ? (
                        <Typography level="body-xs" sx={{ color: '#9e9e9e', mt: 2 }}>
                            Placement profile update is not currently open for your batch.
                        </Typography>
                    ) : null}
                </CardContent>
            </Card>

            {/* Confirmation dialog */}
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 420 }}>
                    <DialogTitle>
                        <WarningRoundedIcon sx={{ color: ORANGE, mr: 1 }} />
                        Confirm Submission
                    </DialogTitle>
                    <DialogContent>
                        <Typography level="body-md" sx={{ mb: 1 }}>
                            Are you sure you want to submit your placement preferences?
                        </Typography>
                        <Typography level="body-sm" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                            Once submitted, you will not be able to change these values.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="solid"
                            loading={saving}
                            onClick={async () => { setConfirmOpen(false); await handleSave(); }}
                            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#d06a1a' }, color: '#fff' }}
                        >
                            Yes, Submit
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            disabled={saving}
                            onClick={() => setConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

        </Box>
    );
};

export default PlacementProfileEdit;
